import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Link,
  Menu,
  Image,
  Stack,
  IconButton,
  VStack,
  Drawer,
  CloseButton,
  Text,
} from "@chakra-ui/react";
import { HamburgerIcon } from "../Icons";
import { ROUTES, NAV_LINKS, COURSE_LINKS } from "../../constants/routes";

const SOCIAL_LINKS = [
  { to: "https://www.instagram.com/midas_taj_classes_pvt_ltd/", icon: "bi-instagram", label: "Instagram" },
  { to: "https://www.facebook.com/midastajclasses/", icon: "bi-facebook", label: "Facebook" },
  { to: "https://twitter.com/midastaj?lang=en", icon: "bi-twitter-x", label: "Twitter" },
  { to: "https://www.youtube.com/@MidasTajCoachingClassesPvtLtd", icon: "bi-youtube", label: "YouTube" },
];

const navLinkStyles = {
  px: 3,
  py: 2,
  rounded: "md",
  color: "white",
  fontWeight: "semibold",
  transition: "all 0.2s",
  _hover: { bg: "whiteAlpha.200", color: "#fffb00" },
  _focus: { outline: "none", boxShadow: "none" },
  _focusVisible: { outline: "2px solid", outlineColor: "white", outlineOffset: "2px" },
  _active: { outline: "none", boxShadow: "none" },
};

function NavLink({ to, children, onClick }) {
  return (
    <Link
      as={RouterLink}
      to={to}
      {...navLinkStyles}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const closeMenu = () => setMenuOpen(false);

  const mobileNavContent = (
    <VStack align="stretch" spacing={0} py={2}>
      <NavLink to={ROUTES.HOME} onClick={closeMenu}>Home</NavLink>
      <Box px={3} py={2} color="white" fontWeight="semibold" fontSize="sm">
        Courses
      </Box>
      {COURSE_LINKS.map((item) => (
        <Link
          key={item.path}
          as={RouterLink}
          to={item.path}
          pl={6}
          pr={3}
          py={2}
          color="whiteAlpha.900"
          _hover={{ bg: "whiteAlpha.200", color: "#fffb00" }}
          _focus={{ outline: "none", boxShadow: "none" }}
          _active={{ outline: "none" }}
          onClick={closeMenu}
        >
          {item.label}
        </Link>
      ))}
      {NAV_LINKS.slice(1).map((link) => (
        <NavLink key={link.path} to={link.path} onClick={closeMenu}>
          {link.label}
        </NavLink>
      ))}
      <Flex gap={2} pt={3} mt={3} borderTopWidth="1px" borderColor="whiteAlpha.300">
        {SOCIAL_LINKS.map(({ to, icon, label }) => (
          <Link
            key={label}
            href={to}
            isExternal
            aria-label={label}
            w="36px"
            h="36px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            rounded="md"
            color="white"
            _hover={{ bg: "whiteAlpha.200", color: "#fffb00" }}
            _focus={{ outline: "none", boxShadow: "none" }}
          >
            <i className={`bi ${icon}`} style={{ fontSize: "1.2rem" }} />
          </Link>
        ))}
      </Flex>
    </VStack>
  );

  return (
    <>
      {/* Header bar: always visible on all viewports (mobile, tablet, desktop) */}
      <Box
        bg="#b70031"
        shadow="md"
        position="sticky"
        top={0}
        zIndex={10}
        pt="env(safe-area-inset-top)"
        minHeight={{ base: "56px", md: "56px" }}
        sx={{ WebkitTapHighlightColor: "transparent" }}
      >
        <Box maxW="1100px" mx="auto" px={{ base: 4, md: 6 }}>
          <Flex h={14} align="center" justify="space-between" minH="44px">
            <Link
              as={RouterLink}
              to={ROUTES.HOME}
              display="flex"
              alignItems="center"
              _focus={{ outline: "none", boxShadow: "none" }}
              _focusVisible={{ outline: "2px solid", outlineColor: "white", outlineOffset: "2px" }}
            >
              <Image
                src={`${process.env.PUBLIC_URL || ""}/logo1.png`}
                h={{ base: "34px", md: "38px" }}
                alt="MIDAS TAJ CLASSES"
                ignoreFallback
              />
            </Link>

            {/* Hamburger: visible on mobile & tablet (below lg). Chakra v3 IconButton uses children for the icon. */}
            <IconButton
              colorScheme="none"
              display={{ base: "flex", lg: "none" }}
              onClick={() => setMenuOpen(true)}
              variant="ghost"
              color="white"
              size="lg"
              minW="44px"
              minH="44px"
              aria-label="Open menu"
              _hover={{ bg: "whiteAlpha.200", color: "white" }}
              _focus={{ outline: "none", boxShadow: "none" }}
              _active={{ outline: "none" }}
            >
              <HamburgerIcon fill="white" width={24} height={24} />
            </IconButton>

            {/* Desktop nav: visible from lg and up */}
            <Box display={{ base: "none", lg: "block" }}>
              <Stack direction="row" spacing={0} align="center">
                <NavLink to={ROUTES.HOME}>Home</NavLink>
                <Menu.Root positioning={{ sameWidth: true }}>
                  <Menu.Trigger
                    asChild
                    style={{ outline: "none" }}
                  >
                    <Link
                      px={3}
                      py={2}
                      rounded="md"
                      color="white"
                      fontWeight="semibold"
                      cursor="pointer"
                      _hover={{ bg: "whiteAlpha.200", color: "#fffb00" }}
                      _focus={{ outline: "none", boxShadow: "none" }}
                      _focusVisible={{ outline: "2px solid", outlineColor: "white", outlineOffset: "2px" }}
                      _active={{ outline: "none" }}
                    >
                      Course ▾
                    </Link>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content bg="white" border="none" shadow="lg" borderRadius="md" py={1} minW="180px">
                      {COURSE_LINKS.map((item) => (
                        <Menu.Item key={item.path} value={item.path} asChild>
                          <Link
                            as={RouterLink}
                            to={item.path}
                            color="gray.800"
                            _hover={{ bg: "gray.50" }}
                            _highlighted={{ bg: "gray.50" }}
                            _focus={{ outline: "none", bg: "gray.50" }}
                            display="block"
                            px={4}
                            py={2}
                          >
                            {item.label}
                          </Link>
                        </Menu.Item>
                      ))}
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
                {NAV_LINKS.slice(1).map((link) => (
                  <NavLink key={link.path} to={link.path}>
                    {link.label}
                  </NavLink>
                ))}
                <Flex gap={1} pl={2}>
                  {SOCIAL_LINKS.map(({ to, icon, label }) => (
                    <Link
                      key={label}
                      href={to}
                      isExternal
                      aria-label={label}
                      w="36px"
                      h="36px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      rounded="md"
                      color="white"
                      _hover={{ bg: "whiteAlpha.200", color: "#fffb00" }}
                      _focus={{ outline: "none", boxShadow: "none" }}
                    >
                      <i className={`bi ${icon}`} style={{ fontSize: "1.2rem" }} />
                    </Link>
                  ))}
                </Flex>
              </Stack>
            </Box>
          </Flex>
        </Box>
      </Box>

      {/* Mobile & tablet: slide-out drawer menu (production-ready) */}
      <Drawer.Root
        open={menuOpen}
        onOpenChange={(e) => setMenuOpen(e.open)}
      >
        <Drawer.Backdrop bg="blackAlpha.600" />
        <Drawer.Positioner
          position="fixed"
          top={0}
          right={0}
          bottom={0}
          left={0}
          display="flex"
          justifyContent="flex-end"
          zIndex={20}
          pt="env(safe-area-inset-top)"
          pb="env(safe-area-inset-bottom)"
        >
          <Drawer.Content
            bg="#b70031"
            color="white"
            shadow="xl"
            overflowY="auto"
            borderLeftWidth="1px"
            borderColor="whiteAlpha.200"
            w={{ base: "min(320px, 100%)", sm: "320px", md: "360px" }}
            maxW="100%"
            h="100%"
          >
            <Drawer.Header
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={4}
              px={4}
              borderBottomWidth="1px"
              borderColor="whiteAlpha.200"
            >
              <Drawer.Title asChild>
                <Text fontWeight="bold" fontSize="lg">
                  Menu
                </Text>
              </Drawer.Title>
              <Drawer.CloseTrigger asChild>
                <CloseButton
                  size="lg"
                  color="white"
                  aria-label="Close menu"
                  _hover={{ bg: "whiteAlpha.200" }}
                  _focus={{ outline: "none", boxShadow: "none" }}
                />
              </Drawer.CloseTrigger>
            </Drawer.Header>
            <Drawer.Body px={4} py={4}>
              {mobileNavContent}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    </>
  );
}
